import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Shield, Sparkles, Car } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950"></div>
        
        {/* Background image */}
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://customer-assets.emergentagent.com/job_alinsideout-detail/artifacts/whq1f0sv_IMG_1272.jpeg"
            alt="Alinsideout detailing service"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 tracking-tight">
            <span className="text-zinc-100">Alin</span>
            <span className="text-zinc-400">side</span>
            <span className="text-zinc-100">out</span>
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 mb-12 font-light">
            Premium Mobile Detailing Services
          </p>
          <Link to="/booking">
            <Button className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200 px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105">
              Book Appointment
            </Button>
          </Link>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-zinc-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 px-4 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-6 text-zinc-100">About Us</h2>
              <p className="text-lg text-zinc-400 mb-6 leading-relaxed">
                Alinsideout is your premier mobile detailing service, bringing professional car care directly to your location. We specialize in transforming vehicles with meticulous attention to detail, using premium products and techniques.
              </p>
              <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
                Our passion is making your vehicle look its absolute best, inside and out. From luxury sedans to everyday drivers, we treat every car with the same level of care and precision.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-zinc-100 mb-2">1000+</div>
                  <div className="text-sm text-zinc-500">Cars Detailed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-zinc-100 mb-2">5★</div>
                  <div className="text-sm text-zinc-500">Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-zinc-100 mb-2">5+</div>
                  <div className="text-sm text-zinc-500">Years Experience</div>
                </div>
              </div>
            </div>
            <div className="relative h-96 bg-zinc-900 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_alinsideout-detail/artifacts/5g1japoe_IMG_2653.jpeg"
                alt="Alinsideout professional detailing service with luxury vehicles"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/60 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-zinc-100">Why Choose Us</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-zinc-950 p-8 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all duration-300">
              <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-zinc-400" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-zinc-100">Premium Quality</h3>
              <p className="text-zinc-400">We use only the finest products and techniques to ensure your vehicle receives the best care possible.</p>
            </div>
            <div className="bg-zinc-950 p-8 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all duration-300">
              <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center mb-6">
                <Calendar className="w-6 h-6 text-zinc-400" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-zinc-100">Convenient Booking</h3>
              <p className="text-zinc-400">Easy online scheduling that fits your busy lifestyle. We come to you at your preferred location.</p>
            </div>
            <div className="bg-zinc-950 p-8 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all duration-300">
              <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-zinc-400" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-zinc-100">Certified Detailer</h3>
              <p className="text-zinc-400">Professional certification and training ensure your vehicle receives expert care and attention.</p>
            </div>
            <div className="bg-zinc-950 p-8 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-all duration-300">
              <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center mb-6">
                <Car className="w-6 h-6 text-zinc-400" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-zinc-100">Car Enthusiast</h3>
              <p className="text-zinc-400">Passionate about car culture and dedicated to treating every vehicle with the respect it deserves.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-zinc-100">Ready to Transform Your Vehicle?</h2>
          <p className="text-xl text-zinc-400 mb-12">Book your appointment now and experience the Alinsideout difference</p>
          <Link to="/booking">
            <Button className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200 px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105">
              Book Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
